import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { betaMe, betaState } from "@/lib/api";

/**
 * Floating bottom-left "Early Access Preview" badge.
 *
 * Renders only when the visitor is an unlocked beta tester. Dismissible
 * per-session via the X button (state held in sessionStorage so it returns
 * on next visit but not on every page navigation).
 */
export default function BetaBadge() {
  const [show, setShow] = useState(false);
  const [tester, setTester] = useState(null);
  const [daysLeft, setDaysLeft] = useState(null);

  useEffect(() => {
    const dismissed = sessionStorage.getItem("beta_badge_dismissed") === "1";
    Promise.all([betaMe(), betaState().catch(() => null)])
      .then(([m, s]) => {
        if (m?.unlocked && !dismissed) {
          setShow(true);
          setTester(m);
          if (s?.end) {
            try {
              const ms = new Date(s.end).getTime() - Date.now();
              const days = Math.max(0, Math.ceil(ms / 86400000));
              setDaysLeft(days);
            } catch {
              /* noop */
            }
          }
        }
      })
      .catch(() => {});
  }, []);

  if (!show) return null;
  return (
    <div
      data-testid="beta-badge"
      className="fixed bottom-6 left-6 z-[7000] bg-[#080808] text-white pl-4 pr-2 py-2.5 flex items-center gap-3 shadow-lg max-w-[280px]"
      role="status"
      aria-label="Early Access Preview active"
    >
      <Sparkles size={14} className="text-[#9DD4D8] shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#9DD4D8] leading-none">
          Early Access Preview
        </p>
        <p className="text-xs text-white/80 mt-1 truncate">
          {tester?.name ? `Signed in as ${tester.name.split(" ")[0]}` : "Closed beta active"}
        </p>
      </div>
      <button
        type="button"
        onClick={() => {
          sessionStorage.setItem("beta_badge_dismissed", "1");
          setShow(false);
        }}
        aria-label="Dismiss preview badge"
        data-testid="beta-badge-dismiss"
        className="p-1.5 text-white/60 hover:text-white"
      >
        <X size={14} />
      </button>
    </div>
  );
}
